import { useEffect, useState, useSyncExternalStore, type KeyboardEvent } from 'react';
import { openImage } from './ImageViewer';
import { avatarToDraw } from '../lib/avatarSrc';
import { conversationPhoto } from '../lib/channelLinks';

// A person's round avatar: initials, with their profile photo on top when they have one and the viewer may see
// it (the server answers 404 otherwise, and the initials stay). Photos are cached briefly by the browser; after
// changing your own, `refreshAvatar` makes every place show the new one at once.

const versions = new Map<string, number>();
const listeners = new Set<() => void>();
export function refreshAvatar(userId: string): void {
  versions.set(userId, Date.now());
  listeners.forEach((l) => l());
}
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function initialsOf(name: string): string {
  // Only words that start with a letter or digit count, so emoji in a group name ("Aile 🏡") never become an initial.
  const parts = name.trim().split(/\s+/).filter((p) => /^[\p{L}\p{N}]/u.test(p));
  const letters = parts.length > 1 ? [parts[0]!, parts[parts.length - 1]!] : parts;
  return letters.map((p) => Array.from(p)[0]?.toLocaleUpperCase() ?? '').join('') || '?';
}

const TONES = 8;
/** A stable colour for someone without a photo, so a list of initials is easy to tell apart. */
export function avatarTone(key: string): number {
  let h = 0;
  for (const ch of key) h = (h * 31 + ch.codePointAt(0)!) >>> 0;
  return h % TONES;
}

/** Addresses that had no photo: not asked again on this page (every chat row would otherwise ask). */
const noPhoto = new Set<string>();
/** Photos already shown once: drawn at once on the next page (no lazy wait, no initials flash). */
const shown = new Set<string>();


/**
 * A group's or channel's photo address (null when it has none: initials only, nothing asked). The change time is
 * part of the address, so a new photo shows everywhere as soon as the conversation updates.
 */
export function groupPhoto(c: { id: string; group: { avatarUpdatedAt: string | null } | null } | null | undefined, large = false): string | null {
  return c ? conversationPhoto(c.id, c.group?.avatarUpdatedAt, large) : null;
}

// The same address for lists that are not my chats (the channel directory, a link page, a link card).
export { conversationPhoto };

export function UserAvatar({
  userId,
  name,
  large = false,
  className = '',
  photo,
  zoomable = false,
}: {
  userId: string | null | undefined;
  name: string;
  large?: boolean;
  className?: string;
  /** A photo address other than a person's (a group photo); initials stay underneath. */
  photo?: string | null;
  /** A tap on the photo (when there is one) opens it large, as WhatsApp does in a profile. */
  zoomable?: boolean;
}) {
  const version = useSyncExternalStore(subscribe, () => (userId ? (versions.get(userId) ?? 0) : 0));
  const [failed, setFailed] = useState<string | null>(null);
  const wanted = photo ?? (userId ? `/api/v1/users/${userId}/avatar?size=${large ? 'lg' : 'sm'}${version ? `&v=${version}` : ''}` : null);
  // A new address for the same photo place (a changed photo, a new version): the one on screen stays until the
  // new one is in the browser's cache, then they swap in one frame.
  const [drawn, setDrawn] = useState(wanted);
  const src = avatarToDraw(
    drawn,
    wanted,
    (s) => shown.has(s) || noPhoto.has(s),
    (s) => shown.has(s) && !noPhoto.has(s) && failed !== s,
  );
  if (src !== drawn) setDrawn(src);
  useEffect(() => {
    if (!wanted || wanted === src) return;
    let alive = true;
    const img = new Image();
    const done = () => {
      if (!alive) return;
      shown.add(wanted);
      setDrawn(wanted);
    };
    img.onload = done;
    img.onerror = () => {
      noPhoto.add(wanted);
      if (alive) setDrawn(wanted);
    };
    img.src = wanted;
    return () => {
      alive = false;
    };
  }, [wanted, src]);
  const [loaded, setLoaded] = useState<string | null>(null);
  const canZoom = zoomable && src !== null && loaded === src && failed !== src;
  const zoom = () => {
    if (canZoom) openImage({ src: src.replace('size=sm', 'size=lg'), name });
  };
  return (
    <span
      className={`avatar avatar-tone-${avatarTone(userId ?? name)}${large ? ' avatar-lg' : ''}${canZoom ? ' is-zoomable' : ''}${className ? ` ${className}` : ''}`}
      aria-hidden={!canZoom}
      {...(canZoom
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-label': name,
            title: name,
            onClick: zoom,
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                zoom();
              }
            },
          }
        : {})}
    >
      {initialsOf(name)}
      {src && failed !== src && !noPhoto.has(src) && (
        <img
          src={src}
          alt=""
          width={large ? 160 : 48}
          height={large ? 160 : 48}
          loading={shown.has(src) ? 'eager' : 'lazy'}
          // A photo seen before is in the cache: drawn in the same frame as the row (async decoding would leave
          // one frame of initials, the "blink" when a row or header is drawn again).
          decoding={shown.has(src) ? 'sync' : 'async'}
          draggable={false}
          onLoad={() => {
            shown.add(src);
            setLoaded(src);
          }}
          onError={() => {
            noPhoto.add(src);
            setFailed(src);
          }}
        />
      )}
    </span>
  );
}
